// Phase 7.0 Stage 2 — Card primitive with a frosted-glass variant.
//
// Two surface treatments share one source of truth (CARD_VARIANT_CLASS):
//   solid — the existing opaque card (bg-card + border). Default;
//           backward-compatible with every pre-7.0 card.
//   glass — translucent + backdrop blur, so the Stage 1 ambient
//           gradient/orb reads THROUGH the surface. The saturate(1.8)
//           boost keeps colors from washing out under the blur; the
//           1px inset top highlight gives the "polished edge" look.
//
// Corners stay sharp to match the app's existing card aesthetic
// (Stage 2 is the background/surface system, not a radius redesign).
//
// Padding/layout are intentionally NOT imposed here — callers pass
// their own via className, so migrating a surface to <Card> never
// changes its spacing. Interactive surfaces that can't be a <div>
// (the Chooser <Link> tiles, Modal/SidePanel panels that hold refs)
// import CARD_VARIANT_CLASS directly instead of nesting a <Card>.

import { type HTMLAttributes, type ReactNode } from "react";

export type CardVariant = "solid" | "glass";

// The frosted-glass surface, WITHOUT any box-shadow. Shared base so
// every glass surface (cards, modals, panels, popovers) blurs +
// tints identically. saturate(1.8) keeps colors from washing out
// under the blur. Callers add their own shadow: cards get a 1px
// inset top highlight (CARD_VARIANT_CLASS.glass); overlays add an
// elevation drop-shadow instead.
export const GLASS_SURFACE = [
  "bg-white/[0.04]",
  "backdrop-blur-[20px] backdrop-saturate-[1.8]",
  "border border-white/[0.06]",
].join(" ");

export const CARD_VARIANT_CLASS: Record<CardVariant, string> = {
  solid: "rounded-2xl " + "bg-theme-bg-card border border-theme-border-default",
  glass: "rounded-2xl " + `${GLASS_SURFACE} shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]`,
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  className?: string;
  children?: ReactNode;
}

export function Card({
  variant = "solid",
  className = "",
  children,
  ...rest
}: CardProps) {
  return (
    <div
      data-card-variant={variant}
      className={`${CARD_VARIANT_CLASS[variant]} ${className}`.trim()}
      {...rest}
    >
      {children}
    </div>
  );
}
